/**
 * @Author: wuyang
 * @Date: 2024/2/6
 * @Description: ""
 */
import { IAtomMethod } from '@/helper/store/createStore'
import {
	BaseAtomMethodClass,
	IBaseEventMitt
} from '@/helper/base/BaseAtomMethodClass'
import { atom } from 'jotai'
import { IBaseAbstractClassProps } from '@/helper/base/BaseAbstractClass'

export type TListenItemParams<K extends any, T extends any> = {
	itemId: K
	newValue: T | undefined
	oldValue: T | undefined
	action: 'addItem' | 'updateItem' | 'delItem'
}

export interface IListenItemMethod<K extends any, T extends any> {
	(params: TListenItemParams<K, T>): void
}

interface IAtomManagerMittProps<K extends any, T> extends IBaseEventMitt {
	listenAtomMap: TListenItemParams<K, T>
}

interface IAtomManagerProps<K extends any, T extends any>
	extends IBaseAbstractClassProps<IAtomManagerMittProps<K, T>> {
	initData?: Map<K, IAtomMethod<T>>
}
export class AtomManager<
	K extends any,
	T extends any
> extends BaseAtomMethodClass<
	IAtomManagerProps<K, T>,
	IAtomManagerMittProps<K, T>
> {
	private __AtomManagerData__: IAtomMethod<Map<K, IAtomMethod<T>>>
	constructor(props: IAtomManagerProps<K, T>) {
		super(props)
		const initData = props.initData ?? new Map<K, IAtomMethod<T>>()
		this.__AtomManagerData__ = this.genAtomMethod(atom(initData))
	}

	addItem(key: K, data: T) {
		const currentAtomMap = this.__AtomManagerData__.get()
		if (currentAtomMap.has(key)) {
			this.updateItem(key, data)
		} else {
			currentAtomMap.set(key, this.genAtomMethod(atom(data)))
			this.__AtomManagerData__.set(new Map(currentAtomMap))
			this.mitt.emit('listenAtomMap', {
				itemId: key,
				action: 'addItem',
				oldValue: undefined,
				newValue: data
			})
		}
	}

	updateItem(key: K, data: T) {
		const currentAtomMap = this.__AtomManagerData__.get()
		const currentData = currentAtomMap.get(key)?.get()
		currentAtomMap.get(key)?.set(data)
		this.mitt.emit('listenAtomMap', {
			itemId: key,
			action: 'updateItem',
			oldValue: currentData,
			newValue: data
		})
	}
	delItem(key: K) {
		const currentAtomMap = this.__AtomManagerData__.get()
		const currentData = currentAtomMap.get(key)?.get()
		currentAtomMap.get(key)?.del()
		currentAtomMap.delete(key)
		this.__AtomManagerData__.set(new Map(currentAtomMap))
		this.mitt.emit('listenAtomMap', {
			itemId: key,
			action: 'delItem',
			oldValue: currentData,
			newValue: undefined
		})
	}
	clearItem() {
		const currentAtomMap = this.__AtomManagerData__.get()
		for (const currentAtomMapKey of currentAtomMap) {
			this.delItem(currentAtomMapKey[0])
		}
	}

	getItem(key: K) {
		const currentAtomMap = this.__AtomManagerData__.get()
		return currentAtomMap.get(key)
	}
	getItemForMap() {
		return this.__AtomManagerData__.get()
	}
	getItemForArray() {
		const currentAtomMap = this.__AtomManagerData__.get()
		return Array.from(currentAtomMap).map((item) => item[1])
	}
	listenItem(listenCallback: IListenItemMethod<K, T>) {
		this.mitt.on('listenAtomMap', listenCallback)
		return () => {
			this.mitt.off('listenAtomMap', listenCallback)
		}
	}
}
